import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    UserCog,
    Plus,
    Edit,
    Trash2,
    Shield,
    ShieldCheck,
    Mail,
    Phone,
    Loader2,
    Search,
    AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminEmployees = () => {
    const { token, user: currentUser } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal states
    const [addModal, setAddModal] = useState(false);
    const [editModal, setEditModal] = useState({ open: false, employee: null });
    const [deleteModal, setDeleteModal] = useState({ open: false, employee: null });
    const [processing, setProcessing] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        phone: '',
        role: 'employee'
    });

    const fetchEmployees = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/employees`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast.error('Erreur lors du chargement des employés');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const handleAddEmployee = async () => {
        if (!formData.email || !formData.password || !formData.name) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        
        setProcessing(true);
        try {
            await axios.post(`${API_URL}/admin/employees`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Employé ajouté avec succès');
            setAddModal(false);
            setFormData({ email: '', password: '', name: '', phone: '', role: 'employee' });
            fetchEmployees();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
        } finally {
            setProcessing(false);
        }
    };

    const handleEditEmployee = async () => {
        if (!editModal.employee) return;
        
        setProcessing(true);
        try {
            await axios.put(
                `${API_URL}/admin/employees/${editModal.employee.id}`,
                {
                    name: formData.name,
                    phone: formData.phone,
                    role: formData.role,
                    ...(formData.password && { password: formData.password })
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Employé mis à jour');
            setEditModal({ open: false, employee: null });
            fetchEmployees();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteEmployee = async () => {
        if (!deleteModal.employee) return;
        
        setProcessing(true);
        try {
            await axios.delete(`${API_URL}/admin/employees/${deleteModal.employee.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Employé supprimé');
            setDeleteModal({ open: false, employee: null });
            fetchEmployees();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setProcessing(false);
        }
    };

    const openEditModal = (employee) => {
        setFormData({
            email: employee.email,
            password: '',
            name: employee.name || '',
            phone: employee.phone || '',
            role: employee.role || 'employee'
        });
        setEditModal({ open: true, employee });
    };

    const getRoleBadge = (role) => {
        switch (role) {
            case 'super_admin':
                return <Badge className="bg-purple-100 text-purple-800"><ShieldCheck className="w-3 h-3 mr-1" />Super Admin</Badge>;
            case 'admin':
                return <Badge className="bg-blue-100 text-blue-800"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
            case 'manager':
                return <Badge className="bg-emerald-100 text-emerald-800">Manager</Badge>;
            default:
                return <Badge className="bg-gray-100 text-gray-800">Employé</Badge>;
        }
    };

    const filteredEmployees = employees.filter(emp => 
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                            <UserCog className="w-7 h-7 text-blue-600" />
                            Gestion des employés
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">
                            {employees.length} employé(s) • Gérez les accès administrateurs
                        </p>
                    </div>
                    <Button 
                        onClick={() => {
                            setFormData({ email: '', password: '', name: '', phone: '', role: 'employee' });
                            setAddModal(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvel employé
                    </Button>
                </div>

                {/* Search */}
                <Card>
                    <CardContent className="p-4">
                        <div className="relative max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Rechercher un employé..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Employees List */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left p-4 font-semibold text-slate-700">Employé</th>
                                <th className="text-left p-4 font-semibold text-slate-700">Email</th>
                                <th className="text-left p-4 font-semibold text-slate-700">Téléphone</th>
                                <th className="text-left p-4 font-semibold text-slate-700">Rôle</th>
                                <th className="text-left p-4 font-semibold text-slate-700">Créé le</th>
                                <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredEmployees.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-8 text-center text-slate-500">
                                        <UserCog className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                                        <p>Aucun employé trouvé</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredEmployees.map((employee) => (
                                    <tr key={employee.id} className="hover:bg-slate-50">
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <span className="text-blue-600 font-bold">
                                                        {employee.name?.charAt(0)?.toUpperCase() || employee.email?.charAt(0)?.toUpperCase()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900">{employee.name || '-'}</p>
                                                    {employee.id === currentUser?.id && (
                                                        <Badge className="bg-blue-50 text-blue-600 text-xs">Vous</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2 text-slate-600">
                                                <Mail className="w-4 h-4 text-slate-400" />
                                                {employee.email}
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {employee.phone ? (
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <Phone className="w-4 h-4 text-slate-400" />
                                                    {employee.phone}
                                                </div>
                                            ) : (
                                                <span className="text-slate-400">-</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {getRoleBadge(employee.role)}
                                        </td>
                                        <td className="p-4 text-slate-500">
                                            {employee.created_at 
                                                ? new Date(employee.created_at).toLocaleDateString('fr-FR')
                                                : '-'
                                            }
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEditModal(employee)}
                                                    className="text-slate-600 hover:text-blue-600"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                {employee.id !== currentUser?.id && employee.role !== 'super_admin' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setDeleteModal({ open: true, employee })}
                                                        className="text-slate-600 hover:text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Add Employee Modal */}
                <Dialog open={addModal} onOpenChange={setAddModal}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Plus className="w-5 h-5 text-blue-600" />
                                Nouvel employé
                            </DialogTitle>
                            <DialogDescription>
                                Créez un compte pour un nouvel employé
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Nom complet <span className="text-red-500">*</span></Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Jean Dupont"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email <span className="text-red-500">*</span></Label>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="jean@entreprise.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Mot de passe <span className="text-red-500">*</span></Label>
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder="••••••••"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Téléphone</Label>
                                <Input
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="+33 6 12 34 56 78"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Rôle</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="employee">Employé</SelectItem>
                                        <SelectItem value="manager">Manager</SelectItem>
                                        <SelectItem value="admin">Administrateur</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAddModal(false)}>
                                Annuler
                            </Button>
                            <Button onClick={handleAddEmployee} disabled={processing}>
                                {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Créer l'employé
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit Employee Modal */}
                <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, employee: null })}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Edit className="w-5 h-5 text-blue-600" />
                                Modifier l'employé
                            </DialogTitle>
                            <DialogDescription>
                                {editModal.employee?.email}
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Nom complet</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nouveau mot de passe (laisser vide pour conserver)</Label>
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder="••••••••"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Téléphone</Label>
                                <Input
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Rôle</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                                    disabled={editModal.employee?.role === 'super_admin'}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="employee">Employé</SelectItem>
                                        <SelectItem value="manager">Manager</SelectItem>
                                        <SelectItem value="admin">Administrateur</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditModal({ open: false, employee: null })}>
                                Annuler
                            </Button>
                            <Button onClick={handleEditEmployee} disabled={processing}>
                                {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Modal */}
                <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, employee: null })}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="w-5 h-5" />
                                Supprimer l'employé
                            </DialogTitle>
                            <DialogDescription>
                                Êtes-vous sûr de vouloir supprimer <strong>{deleteModal.employee?.name || deleteModal.employee?.email}</strong> ?
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="py-4">
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-800">
                                    Cette action est irréversible. L'employé perdra immédiatement l'accès à l'administration.
                                </p>
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteModal({ open: false, employee: null })}>
                                Annuler
                            </Button>
                            <Button variant="destructive" onClick={handleDeleteEmployee} disabled={processing}>
                                {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Supprimer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default AdminEmployees;
